import React, { useRef, useState, useEffect } from "react";
import {
  FaWhatsapp,
  FaPaperclip,
  FaPaperPlane,
  FaTimes,
  FaTrash,
  FaFileAlt,
  FaExpandAlt,
  FaCompressAlt,
} from "react-icons/fa";
import { htmlToWhatsAppText } from "../Utilitie";

const SendWhatsappMsg = ({
  defaultNumber = "",
  defaultMessage = "",
  defaultAttachments = [],
  title = "Send WhatsApp Message",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [phone, setPhone] = useState(defaultNumber);
  const [message, setMessage] = useState(htmlToWhatsAppText(defaultMessage));
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);

  const fileInputRef = useRef(null);
  const messageRef = useRef(null);

  const contacts = [{ name: "rishi", phone: "919088823285" }];

  const [contactSearch, setContactSearch] = useState("");
  const [showContacts, setShowContacts] = useState(false);

  useEffect(() => {
    setMessage(htmlToWhatsAppText(defaultMessage) || "");
  }, [defaultMessage]);
  useEffect(() => {
    setAttachments(defaultAttachments || []);
  }, [defaultAttachments]);
  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
      contact.phone.includes(contactSearch),
  );

  const selectContact = (contact) => {
    setPhone(contact.phone);
    setContactSearch(contact.name);
    setShowContacts(false);
    setError("");
  };
  useEffect(() => {
    if (isOpen) {
      messageRef.current?.focus();
    }
  }, [isOpen]);

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);

    setAttachments((previous) => {
      const combined = [...previous, ...files];
      const unique = combined.filter(
        (file, index, array) =>
          array.findIndex(
            (item) =>
              item.name === file.name &&
              item.size === file.size &&
              item.lastModified === file.lastModified,
          ) === index,
      );

      return unique;
    });

    setError("");
    event.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((previous) => previous.filter((_, i) => i !== index));
  };

  const getWhatsAppNumber = () => {
    // Enter the country code too, e.g. 919876543210.
    return phone.replace(/\D/g, "");
  };

  const getWhatsAppUrl = () => {
    const number = getWhatsAppNumber();
    const url = new URL(`https://wa.me/${number}`);

    if (message.trim()) {
      url.searchParams.set("text", message.trim());
    }

    return url.toString();
  };

  const validate = () => {
    const number = getWhatsAppNumber();

    if (!number || number.length < 8 || number.length > 15) {
      setError("Enter a valid WhatsApp number with country code.");
      return false;
    }

    if (!message.trim() && attachments.length === 0) {
      setError("Enter a message or attach at least one file.");
      return false;
    }

    setError("");
    return true;
  };

  const handleSend = async () => {
    if (!validate()) return;

    const whatsappUrl = getWhatsAppUrl();

    if (attachments.length > 0) {
      const shareData = {
        title: "WhatsApp Attachments",
        text: message.trim(),
        files: attachments,
      };

      // File sharing support varies by browser and operating system.
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: attachments })
      ) {
        try {
          setSharing(true);
          await navigator.share(shareData);
          setError("");
        } catch (err) {
          if (err.name !== "AbortError") {
            setError(
              "Sharing was unsuccessful. Open WhatsApp and attach the files manually.",
            );
          }
        } finally {
          setSharing(false);
        }

        return;
      }

      // Fallback: open WhatsApp and let the user attach files.
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");

      setError(
        "WhatsApp opened. Attach your selected files manually in the chat.",
      );
      return;
    }

    // Text-only message.
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const closeWindow = () => {
    setIsOpen(false);
    setIsExpanded(false);
    setError("");
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      {!isOpen && (
        <button
          type="button"
          className="btn rounded-circle shadow-lg d-flex align-items-center justify-content-center"
          onClick={() => setIsOpen((previous) => !previous)}
          aria-label="Open WhatsApp message window"
          title="Send WhatsApp Message"
          style={{
            //   position: "fixed",
            //   right: "24px",
            //   bottom: "24px",
            width: "38px",
            height: "38px",
            zIndex: 1050,
            backgroundColor: "#25D366",
            fontSize: "20px",
            color: "#fff",
            border: "none",
          }}
        >
          <FaWhatsapp />
        </button>
      )}

      {/* Floating Message Window */}
      {isOpen && (
        <div
          className="card shadow-lg border-0 overflow-hidden"
          style={{
            position: "fixed",
            right: "24px",
            bottom: "94px",
            width: isExpanded
              ? "min(650px, calc(100vw - 32px))"
              : "min(390px, calc(100vw - 32px))",
            height: isExpanded
              ? "min(780px, calc(100vh - 120px))"
              : "min(620px, calc(100vh - 120px))",
            zIndex: 1049,
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div
            className="d-flex align-items-center justify-content-between px-3 py-3 text-white"
            style={{ backgroundColor: "#075E54" }}
          >
            <div className="d-flex align-items-center gap-2">
              <FaWhatsapp size={28} />
              <div>
                <div className="fw-bold">{title}</div>
                <small style={{ opacity: 0.85 }}>
                  Share a message and attachments
                </small>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm text-white"
                title={isExpanded ? "Restore size" : "Expand window"}
                onClick={() => setIsExpanded((previous) => !previous)}
              >
                {isExpanded ? <FaCompressAlt /> : <FaExpandAlt />}
              </button>

              <button
                type="button"
                className="btn btn-sm text-white"
                onClick={closeWindow}
                aria-label="Close"
              >
                <FaTimes size={18} />
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="card-body p-3 overflow-auto">
            <div className="mb-3">
              <label className="form-label fw-semibold">
                WhatsApp Number/Contact
              </label>

              <div className="mb-3 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search contact or enter phone number"
                  value={showContacts ? contactSearch : contactSearch || phone}
                  onFocus={() => {
                    setContactSearch("");
                    setShowContacts(true);
                  }}
                  onChange={(event) => {
                    const value = event.target.value;
                    setContactSearch(value);
                    setPhone(value.replace(/\D/g, ""));
                    setShowContacts(true);
                    setError("");
                  }}
                />

                {showContacts && (
                  <div
                    className="position-absolute bg-white border rounded-3 shadow w-100 mt-1"
                    style={{
                      zIndex: 1100,
                      maxHeight: "220px",
                      overflowY: "auto",
                    }}
                  >
                    {filteredContacts.length > 0 ? (
                      filteredContacts.map((contact) => (
                        <button
                          key={contact.phone}
                          type="button"
                          className="btn btn-light w-100 text-start rounded-0 border-bottom p-2"
                          onClick={() => selectContact(contact)}
                        >
                          <div className="fw-semibold">{contact.name}</div>
                          <small className="text-muted">{contact.phone}</small>
                        </button>
                      ))
                    ) : (
                      <div className="p-3 text-muted small">
                        No matching contacts. You can enter a number manually.
                      </div>
                    )}
                  </div>
                )}

                {showContacts && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100"
                    style={{ zIndex: 1090 }}
                    onClick={() => setShowContacts(false)}
                  />
                )}
              </div>

              <small className="text-muted">
                Include country code, without the + sign if preferred. Example:
                919876543210
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Message</label>

              <textarea
                ref={messageRef}
                className="form-control"
                rows={isExpanded ? 8 : 5}
                placeholder="Type your WhatsApp message..."
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setError("");
                }}
                style={{ resize: "vertical" }}
              />
            </div>

            {/* Attachment Picker */}
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label fw-semibold mb-0">
                  Attachments
                </label>

                <span className="badge text-bg-light border">
                  {attachments.length} file(s)
                </span>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                multiple
                onChange={handleFiles}
                accept="*/*"
              />

              <button
                type="button"
                className="btn btn-outline-success w-100 py-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaPaperclip className="me-2" />
                Add Attachments
              </button>

              {attachments.length > 0 && (
                <div className="mt-2">
                  {attachments.map((file, index) => (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="d-flex align-items-center justify-content-between border rounded-3 p-2 mb-2"
                    >
                      <div className="d-flex align-items-center gap-2 overflow-hidden">
                        <FaFileAlt
                          className="text-success flex-shrink-0"
                          size={20}
                        />

                        <div className="overflow-hidden">
                          <div
                            className="text-truncate fw-semibold small"
                            title={file.name}
                          >
                            {file.name}
                          </div>
                          <small className="text-muted">
                            {file.size < 1024 * 1024
                              ? `${(file.size / 1024).toFixed(1)} KB`
                              : `${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                          </small>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger flex-shrink-0 ms-2"
                        onClick={() => removeAttachment(index)}
                        title="Remove attachment"
                        aria-label={`Remove ${file.name}`}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="alert alert-warning py-2 small mb-3" role="alert">
                {error}
              </div>
            )}

            <div className="alert alert-light border small text-muted mb-0">
              <strong>Note:</strong> WhatsApp may ask you to choose a chat or
              confirm sharing. If file sharing is unsupported, attach the files
              manually after WhatsApp opens.
            </div>
          </div>

          {/* Footer */}
          <div className="card-footer bg-white p-3 border-top">
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-light border flex-grow-1"
                onClick={() => {
                  setMessage("");
                  setAttachments([]);
                  setError("");
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                Clear
              </button>

              <button
                type="button"
                className="btn text-white flex-grow-1"
                style={{ backgroundColor: "#25D366" }}
                onClick={handleSend}
                disabled={sharing}
              >
                <FaPaperPlane className="me-2" />
                {sharing ? "Sharing..." : "Send WhatsApp"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SendWhatsappMsg;
